import { Injectable } from '@angular/core';
import { params } from '@app/configurations';
import { ApiService } from '@app/core/api/api.service';
import { ApiCommonRoutes, ApiEndpoint } from '@app/core/models/api.models';
import { SubFiltersService } from '@app/core/services/subfilters.service';
import { CustomerState } from '@app/core/store/states/customer/customer.state';
import { RouterNavState } from '@app/core/store/states/router-nav/router-nav.state';
import { encodeSearchTerms } from '@app/core/utilities/url.utilities';
import { ForcedFilter } from '@app/shared/components';
import { defaultToastError } from '@app/shared/operators/default-toast-error.operator';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { Observable, catchError, map, of, switchMap, tap } from 'rxjs';
import { ListItem } from '../models/selector.model';
import { BaseSelectorService } from './base-selector.service';
import { PGsDTO, getPGFilters, isMonitor } from './global-filters.utils';

@Injectable()
export class PgSelectorService extends BaseSelectorService {
  requiredParams = [
    params.GLOBAL_FILTER_PERFORMANCE_GROUP_ID,
    params.GLOBAL_FILTER_PERFORMANCE_GROUP_QUERY,
    params.GLOBAL_FILTER_DEST_PERFORMANCE_GROUP_ID,
    params.GLOBAL_FILTER_DEST_PERFORMANCE_GROUP_QUERY,
  ];
  includedData = ['optimizationType', 'performanceGroupId', 'name'];
  defaultLabel = 'global-filter.group-selector.title-perf';
  defaultSubfiltersType = 'PGDefaultFilter';
  dto = PGsDTO;

  readonly srcSelectedItem$ = this.getSelectedItemStream(params.GLOBAL_FILTER_PERFORMANCE_GROUP_ID);
  readonly destSelectedItem$ = this.getSelectedItemStream(params.GLOBAL_FILTER_DEST_PERFORMANCE_GROUP_ID);

  subfilterOptions = () => this.subFiltersService.getPGSubFilters(isMonitor() ? ['optiType'] : []);

  getSelectedItem(id): Observable<ListItem> {
    if (this.cache.items?.[id]) return of(this.cache.items[id]);
    this.loader$.next(true);
    return this.store.select(CustomerState.customerId).pipe(
      switchMap((customerId) =>
        this.apiService
          .getObject({
            endpoint: ApiEndpoint.Customers,
            include: <any>this.includedData,
            route: [customerId, ApiCommonRoutes.PerformanceGroups, id],
          })
          .pipe(
            map(({ data }: any) => {
              this.loader$.next(false);
              this.cache.items[data.performanceGroupId] = this.dto(data);

              return this.cache.items[data.performanceGroupId];
            }),
            defaultToastError('Failed to fetch selected PGs in the Global Filter. Please try with another selection.'),
            catchError((err) => {
              this.loader$.next(false);
              return of({});
            }),
          ),
      ),
    );
  }

  getItems(
    { query, filters = [], hideInactive },
    offset = 0,
    forDestination?: boolean,
    forcedFilters?: ForcedFilter,
  ): Observable<{ items: ListItem[]; count: number }> {
    this.listLoader$.next(true);
    const state = this.store.selectSnapshot(RouterNavState.queryParams);
    const refiners: any = {
      sortValues: { name: 1 },
      filterValues: {
        ...getPGFilters(filters, hideInactive, this.filterDefaults, state),
        ...this.getForcedFilters(forcedFilters, forDestination),
      },
    };

    if (!!query) {
      refiners.searchFields = 'name,performanceGroupId';
      refiners.searchTerm = encodeSearchTerms(query);
    }

    return this.apiService
      .getList({
        endpoint: ApiEndpoint.Customers,
        route: [this.store.selectSnapshot(CustomerState.customerId), ApiCommonRoutes.PerformanceGroups],
        include: this.includedData,
        ...refiners,
        searchValues: this.getSearchValues(filters),
        limit: this.limit,
        offset: offset,
      })
      .pipe(
        tap(() => this.listLoader$.next(false)),
        map(({ data, meta }) => {
          const items = data.map(this.dto);
          return { items, count: meta.pagination.total };
        }),
        defaultToastError('Failed to fetch PGs. Please try with another selection.'),
        catchError((err) => {
          this.listLoader$.next(false);
          return of({ items: [], count: 0 });
        }),
      );
  }

  constructor(
    translate: TranslateService,
    store: Store,
    apiService: ApiService,
    private subFiltersService: SubFiltersService,
  ) {
    super(translate, store, apiService);
  }
}
